import { m } from "@/paraglide/messages.js";
import { getLocale, locales, type Locale } from "@/paraglide/runtime.js";
import {
  foldSearchText,
  ServiceType,
  ServiceTypeGroup,
  type ServiceTypeEntry,
  SERVICE_TYPES,
} from "@complete-athlete/enums";

/** A catalogue message, in the reader's language unless another is asked for. */
type CatalogueCopy = (inputs?: Record<string, never>, options?: { locale?: Locale }) => string;

/** Each catalogue type in the reader's language: its name and the other words people search it by. */
export const SERVICE_TYPE_COPY: Record<ServiceType, { name: CatalogueCopy; terms: CatalogueCopy }> = {
  [ServiceType.PERSONAL_TRAINING]: {
    name: m.service_type_personal_training,
    terms: m.service_type_personal_training_terms,
  },
  [ServiceType.SMALL_GROUP_TRAINING]: {
    name: m.service_type_small_group_training,
    terms: m.service_type_small_group_training_terms,
  },
  [ServiceType.TRAINING_CONSULTATION]: {
    name: m.service_type_training_consultation,
    terms: m.service_type_training_consultation_terms,
  },
  [ServiceType.STRENGTH_TRAINING]: {
    name: m.service_type_strength_training,
    terms: m.service_type_strength_training_terms,
  },
  [ServiceType.FUNCTIONAL_TRAINING]: {
    name: m.service_type_functional_training,
    terms: m.service_type_functional_training_terms,
  },
  [ServiceType.MOBILITY_TRAINING]: {
    name: m.service_type_mobility_training,
    terms: m.service_type_mobility_training_terms,
  },
  [ServiceType.MEDICAL_TRAINING]: {
    name: m.service_type_medical_training,
    terms: m.service_type_medical_training_terms,
  },
  [ServiceType.CORRECTIVE_EXERCISE]: {
    name: m.service_type_corrective_exercise,
    terms: m.service_type_corrective_exercise_terms,
  },
  [ServiceType.PREGNANCY_TRAINING]: {
    name: m.service_type_pregnancy_training,
    terms: m.service_type_pregnancy_training_terms,
  },
  [ServiceType.SENIOR_TRAINING]: {
    name: m.service_type_senior_training,
    terms: m.service_type_senior_training_terms,
  },
  [ServiceType.YOUTH_TRAINING]: {
    name: m.service_type_youth_training,
    terms: m.service_type_youth_training_terms,
  },
  [ServiceType.EMS_TRAINING]: {
    name: m.service_type_ems_training,
    terms: m.service_type_ems_training_terms,
  },
  [ServiceType.ATHLETIC_PERFORMANCE]: {
    name: m.service_type_athletic_performance,
    terms: m.service_type_athletic_performance_terms,
  },
  [ServiceType.ONLINE_COACHING]: {
    name: m.service_type_online_coaching,
    terms: m.service_type_online_coaching_terms,
  },
  [ServiceType.TRAINING_PLAN]: {
    name: m.service_type_training_plan,
    terms: m.service_type_training_plan_terms,
  },
  [ServiceType.RUNNING_COACHING]: {
    name: m.service_type_running_coaching,
    terms: m.service_type_running_coaching_terms,
  },
  [ServiceType.SWIMMING_COACHING]: {
    name: m.service_type_swimming_coaching,
    terms: m.service_type_swimming_coaching_terms,
  },
  [ServiceType.CYCLING_COACHING]: {
    name: m.service_type_cycling_coaching,
    terms: m.service_type_cycling_coaching_terms,
  },
  [ServiceType.TRIATHLON_COACHING]: {
    name: m.service_type_triathlon_coaching,
    terms: m.service_type_triathlon_coaching_terms,
  },
  [ServiceType.HYROX_TRAINING]: {
    name: m.service_type_hyrox_training,
    terms: m.service_type_hyrox_training_terms,
  },
  [ServiceType.BOXING]: {
    name: m.service_type_boxing,
    terms: m.service_type_boxing_terms,
  },
  [ServiceType.MARTIAL_ARTS]: {
    name: m.service_type_martial_arts,
    terms: m.service_type_martial_arts_terms,
  },
  [ServiceType.CROSSFIT]: {
    name: m.service_type_crossfit,
    terms: m.service_type_crossfit_terms,
  },
  [ServiceType.CALISTHENICS]: {
    name: m.service_type_calisthenics,
    terms: m.service_type_calisthenics_terms,
  },
  [ServiceType.GYM_ACCESS]: {
    name: m.service_type_gym_access,
    terms: m.service_type_gym_access_terms,
  },
  [ServiceType.YOGA]: {
    name: m.service_type_yoga,
    terms: m.service_type_yoga_terms,
  },
  [ServiceType.PILATES]: {
    name: m.service_type_pilates,
    terms: m.service_type_pilates_terms,
  },
  [ServiceType.REFORMER_PILATES]: {
    name: m.service_type_reformer_pilates,
    terms: m.service_type_reformer_pilates_terms,
  },
  [ServiceType.INDOOR_CYCLING]: {
    name: m.service_type_indoor_cycling,
    terms: m.service_type_indoor_cycling_terms,
  },
  [ServiceType.GROUP_FITNESS]: {
    name: m.service_type_group_fitness,
    terms: m.service_type_group_fitness_terms,
  },
  [ServiceType.DANCE_FITNESS]: {
    name: m.service_type_dance_fitness,
    terms: m.service_type_dance_fitness_terms,
  },
  [ServiceType.POLE_DANCE]: {
    name: m.service_type_pole_dance,
    terms: m.service_type_pole_dance_terms,
  },
  [ServiceType.NUTRITION_CONSULTATION]: {
    name: m.service_type_nutrition_consultation,
    terms: m.service_type_nutrition_consultation_terms,
  },
  [ServiceType.MEAL_PLAN]: {
    name: m.service_type_meal_plan,
    terms: m.service_type_meal_plan_terms,
  },
  [ServiceType.NUTRITION_COACHING]: {
    name: m.service_type_nutrition_coaching,
    terms: m.service_type_nutrition_coaching_terms,
  },
  [ServiceType.BODY_COMPOSITION_ANALYSIS]: {
    name: m.service_type_body_composition_analysis,
    terms: m.service_type_body_composition_analysis_terms,
  },
  [ServiceType.SPORTS_NUTRITION]: {
    name: m.service_type_sports_nutrition,
    terms: m.service_type_sports_nutrition_terms,
  },
  [ServiceType.CLINICAL_NUTRITION]: {
    name: m.service_type_clinical_nutrition,
    terms: m.service_type_clinical_nutrition_terms,
  },
  [ServiceType.WEIGHT_MANAGEMENT]: {
    name: m.service_type_weight_management,
    terms: m.service_type_weight_management_terms,
  },
  [ServiceType.CHILD_NUTRITION]: {
    name: m.service_type_child_nutrition,
    terms: m.service_type_child_nutrition_terms,
  },
  [ServiceType.PREGNANCY_NUTRITION]: {
    name: m.service_type_pregnancy_nutrition,
    terms: m.service_type_pregnancy_nutrition_terms,
  },
  [ServiceType.PSYCHODIETETICS]: {
    name: m.service_type_psychodietetics,
    terms: m.service_type_psychodietetics_terms,
  },
  [ServiceType.PHYSIO_CONSULTATION]: {
    name: m.service_type_physio_consultation,
    terms: m.service_type_physio_consultation_terms,
  },
  [ServiceType.ORTHOPAEDIC_REHAB]: {
    name: m.service_type_orthopaedic_rehab,
    terms: m.service_type_orthopaedic_rehab_terms,
  },
  [ServiceType.POST_SURGERY_REHAB]: {
    name: m.service_type_post_surgery_rehab,
    terms: m.service_type_post_surgery_rehab_terms,
  },
  [ServiceType.SPORTS_PHYSIO]: {
    name: m.service_type_sports_physio,
    terms: m.service_type_sports_physio_terms,
  },
  [ServiceType.NEURO_REHAB]: {
    name: m.service_type_neuro_rehab,
    terms: m.service_type_neuro_rehab_terms,
  },
  [ServiceType.PELVIC_FLOOR_PHYSIO]: {
    name: m.service_type_pelvic_floor_physio,
    terms: m.service_type_pelvic_floor_physio_terms,
  },
  [ServiceType.PREGNANCY_PHYSIO]: {
    name: m.service_type_pregnancy_physio,
    terms: m.service_type_pregnancy_physio_terms,
  },
  [ServiceType.CHILD_PHYSIO]: {
    name: m.service_type_child_physio,
    terms: m.service_type_child_physio_terms,
  },
  [ServiceType.JAW_PHYSIO]: {
    name: m.service_type_jaw_physio,
    terms: m.service_type_jaw_physio_terms,
  },
  [ServiceType.MANUAL_THERAPY]: {
    name: m.service_type_manual_therapy,
    terms: m.service_type_manual_therapy_terms,
  },
  [ServiceType.SOFT_TISSUE_THERAPY]: {
    name: m.service_type_soft_tissue_therapy,
    terms: m.service_type_soft_tissue_therapy_terms,
  },
  [ServiceType.DRY_NEEDLING]: {
    name: m.service_type_dry_needling,
    terms: m.service_type_dry_needling_terms,
  },
  [ServiceType.KINESIOTAPING]: {
    name: m.service_type_kinesiotaping,
    terms: m.service_type_kinesiotaping_terms,
  },
  [ServiceType.SHOCKWAVE_THERAPY]: {
    name: m.service_type_shockwave_therapy,
    terms: m.service_type_shockwave_therapy_terms,
  },
  [ServiceType.PHYSICAL_MODALITIES]: {
    name: m.service_type_physical_modalities,
    terms: m.service_type_physical_modalities_terms,
  },
  [ServiceType.OSTEOPATHY]: {
    name: m.service_type_osteopathy,
    terms: m.service_type_osteopathy_terms,
  },
  [ServiceType.CHIROPRACTIC]: {
    name: m.service_type_chiropractic,
    terms: m.service_type_chiropractic_terms,
  },
  [ServiceType.CRANIOSACRAL_THERAPY]: {
    name: m.service_type_craniosacral_therapy,
    terms: m.service_type_craniosacral_therapy_terms,
  },
  [ServiceType.SPORTS_MASSAGE]: {
    name: m.service_type_sports_massage,
    terms: m.service_type_sports_massage_terms,
  },
  [ServiceType.THERAPEUTIC_MASSAGE]: {
    name: m.service_type_therapeutic_massage,
    terms: m.service_type_therapeutic_massage_terms,
  },
  [ServiceType.DEEP_TISSUE_MASSAGE]: {
    name: m.service_type_deep_tissue_massage,
    terms: m.service_type_deep_tissue_massage_terms,
  },
  [ServiceType.RELAXATION_MASSAGE]: {
    name: m.service_type_relaxation_massage,
    terms: m.service_type_relaxation_massage_terms,
  },
  [ServiceType.LYMPHATIC_DRAINAGE]: {
    name: m.service_type_lymphatic_drainage,
    terms: m.service_type_lymphatic_drainage_terms,
  },
  [ServiceType.PRENATAL_MASSAGE]: {
    name: m.service_type_prenatal_massage,
    terms: m.service_type_prenatal_massage_terms,
  },
  [ServiceType.CUPPING]: {
    name: m.service_type_cupping,
    terms: m.service_type_cupping_terms,
  },
};

/** The heading of each shelf of the catalogue. */
export const SERVICE_TYPE_GROUP_COPY: Record<ServiceTypeGroup, () => string> = {
  [ServiceTypeGroup.ONE_TO_ONE]: m.service_type_group_one_to_one,
  [ServiceTypeGroup.ONLINE]: m.service_type_group_online,
  [ServiceTypeGroup.SPORTS]: m.service_type_group_sports,
  [ServiceTypeGroup.CLASSES]: m.service_type_group_classes,
  [ServiceTypeGroup.NUTRITION]: m.service_type_group_nutrition,
  [ServiceTypeGroup.REHAB]: m.service_type_group_rehab,
  [ServiceTypeGroup.THERAPIES]: m.service_type_group_therapies,
  [ServiceTypeGroup.MASSAGE]: m.service_type_group_massage,
};

/** The whole catalogue as `searchServiceTypes` reads it, in the current language. */
const termsIn = (type: ServiceType, locale: Locale) =>
  SERVICE_TYPE_COPY[type]
    .terms({}, { locale })
    .split(",")
    .map((term) => term.trim())
    .filter(Boolean);

/**
 * The catalogue for `searchServiceTypes`, named in the reader's language but found in any: someone
 * on the Polish page can type "massage". The other languages come after the reader's own, so their
 * words only win where the reader's do not fit.
 */
export const serviceTypeEntries = (): ServiceTypeEntry[] => {
  const reading = getLocale();
  const others = locales.filter((locale) => locale !== reading);
  return SERVICE_TYPES.map((type) => ({
    type,
    name: SERVICE_TYPE_COPY[type].name(),
    terms: [
      ...termsIn(type, reading),
      ...others.flatMap((locale) => [SERVICE_TYPE_COPY[type].name({}, { locale }), ...termsIn(type, locale)]),
    ],
  }));
};

/**
 * The service typed words mean in a language other than the reader's. Free words are searched as
 * they are, against what businesses wrote in their own language, so "massage" finds nobody in
 * Poland unless it becomes the service it names.
 */
export const serviceTypeInOtherLanguage = (text: string): ServiceType | undefined => {
  const typed = foldSearchText(text);
  const reading = getLocale();
  const said = (phrase: string) => foldSearchText(phrase) === typed;
  const inReading = (type: ServiceType) =>
    said(SERVICE_TYPE_COPY[type].name()) || termsIn(type, reading).some(said);
  if (!typed || SERVICE_TYPES.some(inReading)) return undefined;
  return locales
    .filter((locale) => locale !== reading)
    .flatMap((locale) =>
      SERVICE_TYPES.filter((type) => said(SERVICE_TYPE_COPY[type].name({}, { locale })) || termsIn(type, locale).some(said)),
    )[0];
};
